import { LineItem } from "../../interfaces/LineItem";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { FloorGroup } from "../../interfaces/AreaSheet";
import { splitLineItemsByDimension } from "../utils/lineItems";
import { getNamedRangeFromWorksheet, setPrintAreaToUsedRange } from "../workbook";
import { getEstimateReportData } from "../workbook";
import { ValidationError } from "../../errors";
import { getReportOptions, updateReportDate } from "./helpers/common";
import { addGroupingMetaData } from "./helpers/groupings";
import { RowFormat, handleDynamicColumns, printFormattedData } from "./helpers/print";
import { calculatedRangeNames, columnNames, rowNames } from "./helpers/rangeNames";
import { cacheableData, worksheetCache } from "../workbook/cache";
import { track } from "../../clients/mixpanel";
import { ColumnNameToIndexMap } from "./helpers/common";
import { getEstimateGroupings } from "./helpers/groupings";
import { createColumnNameToIndexMap } from "./helpers/print";
import { BLANK_LABEL } from "./helpers/common";
import { BLANK_ALTERNATE_LABEL } from "../../interfaces/Alternate";
import { getProcessedEstimate } from "../datasources/estimate";
import { transformUseGroups } from "../datasources/useGroups";

export const markupsDefaultOptions = {
  noGrouping: false,
  groupRows: true,
  subtotalsInHeader: true,
  splitByUse: false,
  freezeGroupLevels: false,
  onlyLevelOneSubtotals: false,
} as MarkupsReportOptions;

export interface MarkupsReportOptions extends cacheableData {
  groupByProps: string[];
  noGrouping: boolean;
  subtotalsInHeader: boolean;
  groupRows: boolean;
  freezeGroupLevels: boolean;
  onlyLevelOneSubtotals: boolean;
  splitByUse: boolean;
}

interface MarkupsReportArrayArgs {
  data: LineItem[];
  columnMap: ColumnNameToIndexMap;
  useGroups?: ReportUseGroup[];
  floorGroups?: FloorGroup[];
  options: MarkupsReportOptions;
}

export const fetchMarkupItemsForReport = async (estimateIdParam?: string): Promise<Record<string, any>[]> => {
  let estimateId = estimateIdParam;
  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];
  const processed = await getProcessedEstimate(estimateId);
  if (!processed) return [];
  return processed.baseBidItems.filter((item) => item.type === "markup" || item.type === "atl_markup");
};

export const generateMarkupsReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const processed = await getProcessedEstimate(estimateId);
  if (!processed) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const markupItems = processed.baseBidItems.filter((item) => item.type === "markup" || item.type === "atl_markup");
  const floorGroups = processed.estimateData.floor_groups || [];
  const useGroups = transformUseGroups(processed.estimateData.estimate.stats.useGroups || [], floorGroups);

  useGroups.sort((a, b) => a.order - b.order);
  floorGroups.sort((a, b) => a.label.localeCompare(b.label));

  const useGroupsRange = await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.useGroups}`);
  if (useGroupsRange) {
    await handleDynamicColumns(worksheet, columnNames.useGroups, useGroups || [], { copyFormat: true });
  }

  const floorGroupsRange = await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.floorGroups}`);
  if (floorGroupsRange) {
    await handleDynamicColumns(
      worksheet,
      columnNames.floorGroups,
      floorGroups.map((fg) => ({ ...fg, name: fg.label })),
      { copyFormat: true }
    );
  }

  const columnMap = await createColumnNameToIndexMap(worksheet);
  const options = (await getReportOptions(worksheet, markupsDefaultOptions)) as MarkupsReportOptions;

  if (options.noGrouping) {
    options.groupByProps = [];
  } else if (!options.freezeGroupLevels || !options.groupByProps?.length) {
    options.groupByProps = await getEstimateGroupings(estimateId);
  }

  const { dataArray: markupDataArray, formatList: markupFormatList } = markupsReportArray({
    data: markupItems,
    columnMap,
    useGroups: useGroups || [],
    floorGroups: floorGroups || [],
    options,
  });

  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: markupDataArray,
    formatList: markupFormatList,
    worksheet,
    insertAtRangeName: rowNames.markups,
    startColumnIndex,
    options,
    columnMap,
  });

  // cache last groupByProps for the report
  if (!options.noGrouping) {
    await worksheetCache.setOptions(worksheet, { groupByProps: options.groupByProps });
  }

  //if the report grouping named range exists, add the grouping metadata
  await addGroupingMetaData(worksheet, options.noGrouping ? ["None"] : options.groupByProps);
  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);

  track("excel.createReport.markups", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

const splitMarkupItems = (
  items: LineItem[],
  useGroups: ReportUseGroup[],
  floorGroups: FloorGroup[],
  groupByProps: string[]
): LineItem[] => {
  // Store original quantities (markup percentages) before splitting
  const originalQuantities = new Map(items.map((item) => [item.id, item.quantity]));

  let processedItems = items;

  // Step 1: Split by floor_group
  if (groupByProps.includes("floor_group") && floorGroups.length > 0) {
    processedItems = splitLineItemsByDimension(
      processedItems,
      floorGroups,
      "floor_group",
      "_$$",
      (group) => group.label
    );
  }

  // Step 2: Split by use_group
  if (groupByProps.includes("use_group") && useGroups.length > 0) {
    processedItems = splitLineItemsByDimension(
      processedItems,
      useGroups,
      "use_group",
      "_$",
      (group) => group.name,
      (g, item) => {
        // Only filter by floor_group if we split by building (floor_group is in groupByProps)
        if (groupByProps.includes("floor_group") && floorGroups.length > 0) {
          return g.floor_group === item.floor_group;
        }
        return true;
      }
    );
  }

  // Restore original quantities (markup percentages should not be split)
  processedItems.forEach((item) => {
    const originalQuantity = originalQuantities.get(item.id);
    if (originalQuantity !== undefined) {
      item.quantity = originalQuantity;
    }
  });

  return processedItems;
};

const markupsReportArray = ({
  data,
  columnMap,
  useGroups = [],
  floorGroups = [],
  options,
}: MarkupsReportArrayArgs): { dataArray: (string | number | null)[][]; formatList: RowFormat[] } => {
  const { onlyLevelOneSubtotals, groupByProps, subtotalsInHeader, splitByUse } = options;

  let sortedData = data
    .slice()
    .sort((a, b) => (a.order ?? "").localeCompare(b.order ?? "", undefined, { numeric: true }));

  // Split line items if splitByUse is enabled
  if (splitByUse) {
    sortedData = splitMarkupItems(sortedData, useGroups, floorGroups, groupByProps);
  }

  const formatList: RowFormat[] = [];
  let maxColumnIndex = Math.max(...Object.values(columnMap));
  const indentColumn = columnMap.name;
  const isUseGroupsRequested = columnMap[columnNames.useGroups] !== undefined;
  const hasUseGroups = useGroups.length > 0;
  const isFloorGroupsRequested = columnMap[columnNames.floorGroups] !== undefined;
  const hasFloorGroups = floorGroups.length > 0;

  // add use group columns to column map if requested
  if (isUseGroupsRequested && hasUseGroups) {
    let columnIndex = columnMap[columnNames.useGroups];
    useGroups.forEach((useGroup) => {
      columnMap[useGroup.id] = columnIndex++;
      maxColumnIndex = Math.max(maxColumnIndex, columnMap[useGroup.id]);
    });
  }

  // add floor group columns to column map if requested
  if (isFloorGroupsRequested && hasFloorGroups) {
    let columnIndex = columnMap[columnNames.floorGroups];
    floorGroups.forEach((floorGroup) => {
      columnMap[floorGroup.id] = columnIndex++;
      maxColumnIndex = Math.max(maxColumnIndex, columnMap[floorGroup.id]);
    });
  }

  // Recursive function to group data
  const groupData = (items: LineItem[], level: number): (string | number | null)[][] => {
    if (level >= groupByProps.length) {
      // Base case: last grouping level, return items
      return items.flatMap((item) => {
        const row: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        Object.keys(columnMap).forEach((key) => {
          // Skip use group and floor group columns as they're handled separately
          const isUseGroupColumn = useGroups.some(
            (ug) => key === ug.id || key === `${ug.id}_gsf` || key === `${ug.id}_unit`
          );
          const isFloorGroupColumn = floorGroups.some((fg) => key === fg.id || key === `${fg.id}_gsf`);
          if (!isUseGroupColumn && !isFloorGroupColumn) {
            if (key in item) {
              if (key === columnNames.quantity) {
                row[columnMap[key]] = item.quantity / 100;
              } else {
                row[columnMap[key]] = item[key];
              }
            } else if (key in calculatedRangeNames) {
              row[columnMap[key]] = calculatedRangeNames[key](item);
            }
          }
        });

        if (isUseGroupsRequested) {
          if (hasUseGroups) {
            // Add use group totals
            useGroups.forEach((useGroup) => {
              // When split by use and grouped by floor_group, only include the _$ value
              // if the use group belongs to the current item's floor group
              const useGroupTotal =
                splitByUse && groupByProps.includes("floor_group")
                  ? useGroup.floor_group === item.floor_group
                    ? Number(item[`${useGroup.name}_$`]) || 0
                    : 0
                  : Number(item[`${useGroup.name}_$`]) || 0;

              // Use the relative column index from columnMap
              row[columnMap[useGroup.id]] = useGroupTotal;
            });
          } else {
            row[columnMap[columnNames.useGroups]] = 0;
          }
        }

        if (isFloorGroupsRequested) {
          if (hasFloorGroups) {
            // Add floor group totals calculated from use groups
            floorGroups.forEach((floorGroup) => {
              // When split by use and grouped by floor_group, use the _$$ value directly
              // only if this floor group matches the current item's floor group
              const floorGroupTotal =
                splitByUse && groupByProps.includes("floor_group")
                  ? floorGroup.label === item.floor_group
                    ? Number(item[`${floorGroup.label}_$$`]) || 0
                    : 0
                  : useGroups
                      .filter((ug) => ug.floor_group === floorGroup.label)
                      .reduce((sum, useGroup) => sum + (Number(item[`${useGroup.name}_$`]) || 0), 0);

              // Use the relative column index from columnMap
              row[columnMap[floorGroup.id]] = floorGroupTotal;
            });
          } else {
            row[columnMap[columnNames.floorGroups]] = 0;
          }
        }

        formatList.push({ style: "item", level, indentColumn, group: Array.from({ length: level }, (_, i) => i + 1) });
        return [row];
      });
    }

    const groupByProp = groupByProps[level];

    const grouped = items.reduce(
      (acc, item) => {
        const blankString = groupByProp === "alternate" ? BLANK_ALTERNATE_LABEL : BLANK_LABEL;
        const groupKey = item[groupByProp] || blankString;
        acc[groupKey] = acc[groupKey] || [];
        acc[groupKey].push(item);
        return acc;
      },
      {} as { [key: string]: LineItem[] }
    );

    const result: (string | number | null)[][] = [];
    const sortedGroupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    sortedGroupKeys.forEach((groupKey) => {
      // null leaves a cell as printed, so template formulas filled down from the first row survive.
      const headerRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
      headerRow[0] = groupKey; // headers should always be left-most column

      formatList.push({
        style: "header",
        level,
        indentColumn,
        group: level > 0 ? Array.from({ length: level }, (_, i) => i + 1) : [],
      });

      // Recursively handle nested groups
      const nestedGroup = groupData(grouped[groupKey], level + 1);
      result.push(headerRow, ...nestedGroup);

      if (!onlyLevelOneSubtotals || level === 0) {
        if (subtotalsInHeader) {
          // Add subtotal formula to the header row's total column
          const itemCount = nestedGroup.length;
          headerRow[columnMap.total] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;

          if (isUseGroupsRequested) {
            if (hasUseGroups) {
              // Add subtotal formulas for each use group
              useGroups.forEach((useGroup) => {
                // Use the relative column index from columnMap
                headerRow[columnMap[useGroup.id]] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
              });
            } else {
              headerRow[columnMap[columnNames.useGroups]] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
            }
          }

          if (isFloorGroupsRequested) {
            if (hasFloorGroups) {
              // Add subtotal formulas for each floor group
              floorGroups.forEach((floorGroup) => {
                headerRow[columnMap[floorGroup.id]] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
              });
            } else {
              headerRow[columnMap[columnNames.floorGroups]] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
            }
          }
        } else {
          // Add subtotal row
          const itemCount = nestedGroup.length;
          const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          subtotalRow[columnMap.total - 1] = `${groupKey} Subtotal`; // Subtotal should always be right of total
          subtotalRow[columnMap.total] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

          if (isUseGroupsRequested) {
            if (hasUseGroups) {
              // Add subtotal formulas for each use group
              useGroups.forEach((useGroup) => {
                // Use the relative column index from columnMap
                subtotalRow[columnMap[useGroup.id]] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
              });
            } else {
              subtotalRow[columnMap[columnNames.useGroups]] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
            }
          }

          if (isFloorGroupsRequested) {
            if (hasFloorGroups) {
              // Add subtotal formulas for each floor group
              floorGroups.forEach((floorGroup) => {
                subtotalRow[columnMap[floorGroup.id]] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
              });
            } else {
              subtotalRow[columnMap[columnNames.floorGroups]] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
            }
          }

          result.push(subtotalRow);
          formatList.push({ style: "subtotal", level, group: Array.from({ length: level + 1 }, (_, i) => i + 1) });
        }
      }
    });

    return result;
  };

  // Start grouping from level 0
  const dataArray = groupData(sortedData, 0);
  return { dataArray, formatList };
};
